/**
 * HowItWorksRadial — "How LitCurrent works" infographic.
 * Collapsible accordion variant for Feature Hub (collapsed by default).
 */
import React, { useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

interface HowItWorksRadialProps {
  variant?: 'full' | 'compact';
  collapsible?: boolean;
  /** When collapsible, starts open if true. Defaults to collapsed. */
  defaultExpanded?: boolean;
  className?: string;
}

const InfographicImage: React.FC = () => (
  <img
    src="/images/how-litpulse-works.jpeg"
    alt="How LitCurrent works — Preferences, LitScreen, LitHub, LitStream, LitClub, LitBridge, LitScholar workflow"
    className="w-full rounded-lg border border-slate-100"
    data-testid="how-it-works-image"
  />
);

export const HowItWorksRadial: React.FC<HowItWorksRadialProps> = ({
  variant = 'full',
  collapsible = false,
  defaultExpanded = false,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  if (collapsible) {
    return (
      <div
        className={cn(
          'w-full rounded-xl border border-sky-100 bg-gradient-to-r from-sky-50/80 via-white to-violet-50/40',
          'shadow-sm overflow-hidden',
          className
        )}
        data-testid="how-it-works-radial"
      >
        <button
          type="button"
          onClick={() => setIsExpanded((open) => !open)}
          aria-expanded={isExpanded}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 sm:px-6 text-left hover:bg-white/50 transition-colors"
          data-testid={isExpanded ? 'how-it-works-trigger' : 'how-it-works-collapsed'}
        >
          <span className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-white/90 border border-sky-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-sky-700 shrink-0 shadow-sm">
              <Sparkles className="w-3.5 h-3.5" />
              How LitCurrent works
            </span>
            {variant === 'full' && (
              <span className="text-sm text-slate-500 font-normal truncate hidden sm:inline">
                Your continuous learning journey
              </span>
            )}
          </span>
          <ChevronDown
            className={cn(
              'w-5 h-5 shrink-0 text-slate-400 transition-transform duration-200',
              isExpanded && 'rotate-180'
            )}
          />
        </button>

        {isExpanded && (
          <div
            className="px-5 pb-5 sm:px-6 sm:pb-6 pt-1 border-t border-sky-100/80"
            data-testid="how-it-works-content"
          >
            <InfographicImage />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn('relative', className)} data-testid="how-it-works-radial">
      <h2 className="text-lg font-bold text-slate-900 mb-4">How LitCurrent works</h2>
      <InfographicImage />
    </div>
  );
};

export default HowItWorksRadial;
